import type { Path } from "@/router";
import DetailsCard from "@/components/DetailsCard";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import MetricGrid from "@/components/MetricGrid";
import SectionHeading from "@/components/SectionHeading";

import type { CareerPlanCompetencyScore } from "./careerPlanHistory";
import { getArchetypeStrengthScores, type ExperienceArchetype } from "./experienceIndexArchetypes";
import { getExperienceIndexStrengthCopy } from "./experienceIndexCompetencyCopy";
import { getExperienceIndexCompetencyIcon } from "./experienceIndexIcons";

const ARCHETYPE_HEADING = {
  heading: "Archetype",
  body: "Named profile from this person's completed answers. Independent of comparison role."
};

const TOP_COMPETENCIES_HEADING = "Top Competencies";

export default function ExperienceIndexArchetypeSection({
  archetype,
  scores,
  onSelectCompetency
}: {
  archetype: ExperienceArchetype;
  scores: CareerPlanCompetencyScore[];
  onSelectCompetency?: (competencyId: string) => void;
}) {
  const strengths = getArchetypeStrengthScores(archetype, scores);
  const metrics: MetricCardType<Path>[] = strengths.map((strength) => ({
    iconName: getExperienceIndexCompetencyIcon(strength.competencyName),
    value: strength.competencyName,
    label: getExperienceIndexStrengthCopy(strength.competencyName),
    testId: `career-plan-ei-archetype-strength-${strength.competencyId}`,
    asButton: Boolean(onSelectCompetency),
    onClick: onSelectCompetency ? () => onSelectCompetency(strength.competencyId) : undefined
  }));

  return (
    <div data-testid="career-plan-ei-archetype">
      <SectionHeading heading={ARCHETYPE_HEADING.heading} body={ARCHETYPE_HEADING.body} />
      <div data-testid="career-plan-ei-archetype-title">
        <DetailsCard heading={archetype.title} testId="career-plan-ei-archetype-card">
          <p className="text-body-medium text-sys-brand-on-surface" data-testid="career-plan-ei-archetype-growth-edge">
            <span className="text-body-medium-semibold">Growth Edge:</span> {archetype.growthEdgeName}
          </p>
          <p
            className="mt-2 text-body-medium text-sys-brand-on-surface-variant"
            data-testid="career-plan-ei-archetype-description"
          >
            {archetype.description}
          </p>
        </DetailsCard>
      </div>
      {metrics.length > 0 ? (
        <div data-testid="career-plan-ei-archetype-strengths">
          <SectionHeading heading={TOP_COMPETENCIES_HEADING} />
          <MetricGrid metrics={metrics} layout="thirds" hasHeading />
        </div>
      ) : null}
    </div>
  );
}
